import { useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@/app/container';
import { SalesRepositoryToken } from '../../di/tokens';
import type {
  CancelBillingAgreementInput,
  CreateDebtArrangementInput,
  ActivateDebtArrangementInput,
  UpdateDebtArrangementDraftInput,
  DebtArrangementReasonInput,
  AbandonContractInput,
  ResolveCycleCountInput,
  SyncChargeHistoryInput,
  JustifiedActionInput,
  ManuallySettleInvoiceInput,
} from '../../domain/entities/sales.entity';

/**
 * Hooks de ação (mutations) sobre BA e DA. As mesmas ações servem tanto ao
 * detalhe da transação quanto ao do contrato: por isso recebem um
 * `invalidateKey` explícito (o prefixo da query da entidade que as originou —
 * `['sales','transaction', id]` ou `['sales','contract', id]`), invalidado no
 * sucesso para detalhe, abas e histórico refletirem o novo estado.
 */
function useInvalidate(invalidateKey: readonly unknown[]) {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: invalidateKey });
}

export function useCancelBillingAgreement(invalidateKey: readonly unknown[]) {
  const repository = container.resolve(SalesRepositoryToken);
  const invalidate = useInvalidate(invalidateKey);
  return useMutation({
    mutationFn: (vars: {
      billingAgreementId: string;
      body: CancelBillingAgreementInput;
    }) => repository.cancelBillingAgreement(vars.billingAgreementId, vars.body),
    onSuccess: invalidate,
  });
}

/**
 * Resolve o total de ciclos da assinatura pela API do gateway (T2). Invalida a
 * entidade inteira: o desfecho pode mudar o modo da BA e criar as invoices dos
 * ciclos futuros, que aparecem na aba de invoices.
 */
export function useResolveBillingAgreementCycleCount(
  invalidateKey: readonly unknown[],
) {
  const repository = container.resolve(SalesRepositoryToken);
  const invalidate = useInvalidate(invalidateKey);
  return useMutation({
    mutationFn: (vars: {
      billingAgreementId: string;
      body: ResolveCycleCountInput;
    }) =>
      repository.resolveBillingAgreementCycleCount(
        vars.billingAgreementId,
        vars.body,
      ),
    onSuccess: invalidate,
  });
}

/**
 * Reconstrói as cobranças que o gateway teve e nunca chegaram até nós. Invalida
 * a entidade inteira: a reconciliação cria faturas e transações, que aparecem na
 * tabela de invoices, no valor total e no histórico de auditoria.
 */
export function useSyncBillingAgreementChargeHistory(
  invalidateKey: readonly unknown[],
) {
  const repository = container.resolve(SalesRepositoryToken);
  const invalidate = useInvalidate(invalidateKey);
  return useMutation({
    mutationFn: (vars: {
      billingAgreementId: string;
      body: SyncChargeHistoryInput;
    }) =>
      repository.syncBillingAgreementChargeHistory(
        vars.billingAgreementId,
        vars.body,
      ),
    onSuccess: invalidate,
  });
}

/** O mesmo, com o alcance da página do contrato: as BAs que têm dessincronia. */
export function useSyncContractChargeHistory(
  invalidateKey: readonly unknown[],
) {
  const repository = container.resolve(SalesRepositoryToken);
  const invalidate = useInvalidate(invalidateKey);
  return useMutation({
    mutationFn: (vars: { contractId: string; body: SyncChargeHistoryInput }) =>
      repository.syncContractChargeHistory(vars.contractId, vars.body),
    onSuccess: invalidate,
  });
}

/**
 * Autosave do rascunho — cria a DA na 1ª gravação. Invalida só a query de
 * offer-and-arrangements (não a entidade inteira) para o cache conhecer o novo
 * rascunho sem disparar refetches que atrapalhem a digitação no painel.
 */
export function useCreateDebtArrangementDraft(
  invalidateKey: readonly unknown[],
) {
  const repository = container.resolve(SalesRepositoryToken);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDebtArrangementInput) =>
      repository.createDebtArrangement(body),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: [...invalidateKey, 'offer-arrangements'],
      }),
  });
}

/** Autosave do rascunho — atualizações subsequentes (sem invalidar; o painel é a fonte). */
export function useUpdateDebtArrangementDraft() {
  const repository = container.resolve(SalesRepositoryToken);
  return useMutation({
    mutationFn: (vars: {
      debtArrangementId: string;
      body: UpdateDebtArrangementDraftInput;
    }) =>
      repository.updateDebtArrangementDraft(vars.debtArrangementId, vars.body),
  });
}

export function useActivateDebtArrangement(invalidateKey: readonly unknown[]) {
  const repository = container.resolve(SalesRepositoryToken);
  const invalidate = useInvalidate(invalidateKey);
  return useMutation({
    mutationFn: (vars: {
      debtArrangementId: string;
      body: ActivateDebtArrangementInput;
    }) => repository.activateDebtArrangement(vars.debtArrangementId, vars.body),
    onSuccess: invalidate,
  });
}

export function useCancelDebtArrangement(invalidateKey: readonly unknown[]) {
  const repository = container.resolve(SalesRepositoryToken);
  const invalidate = useInvalidate(invalidateKey);
  return useMutation({
    mutationFn: (vars: {
      debtArrangementId: string;
      body: DebtArrangementReasonInput;
    }) => repository.cancelDebtArrangement(vars.debtArrangementId, vars.body),
    onSuccess: invalidate,
  });
}

export function useRevertDebtArrangement(invalidateKey: readonly unknown[]) {
  const repository = container.resolve(SalesRepositoryToken);
  const invalidate = useInvalidate(invalidateKey);
  return useMutation({
    mutationFn: (vars: {
      debtArrangementId: string;
      body: DebtArrangementReasonInput;
    }) => repository.revertDebtArrangement(vars.debtArrangementId, vars.body),
    onSuccess: invalidate,
  });
}

/** Abandono manual do contrato (override admin → terminal ABANDONED). */
export function useAbandonContract(invalidateKey: readonly unknown[]) {
  const repository = container.resolve(SalesRepositoryToken);
  const invalidate = useInvalidate(invalidateKey);
  return useMutation({
    mutationFn: (vars: { contractId: string; body: AbandonContractInput }) =>
      repository.abandonContract(vars.contractId, vars.body),
    onSuccess: invalidate,
  });
}

/**
 * Sobe o anexo da justificativa e devolve o `fileId`. Passo separado de
 * propósito: a operação em si recebe só o `fileId` no corpo JSON, então o
 * formulário sobe o arquivo no submit e só então dispara a ação. Não invalida
 * nada — o upload por si não muda nenhuma entidade visível.
 */
export function useUploadSalesAttachment() {
  const repository = container.resolve(SalesRepositoryToken);
  return useMutation({
    mutationFn: (file: File) => repository.uploadAttachment(file),
  });
}

/**
 * Baixa manual da fatura — pagamento que entrou fora do gateway. Invalida a
 * entidade inteira: a transação nova aparece na aba de faturas e pode mudar o
 * status do contrato (mesmo caminho do pagamento aprovado por webhook).
 */
export function useManuallySettleInvoice(invalidateKey: readonly unknown[]) {
  const repository = container.resolve(SalesRepositoryToken);
  const invalidate = useInvalidate(invalidateKey);
  return useMutation({
    mutationFn: (vars: {
      invoiceId: string;
      body: ManuallySettleInvoiceInput;
    }) => repository.manuallySettleInvoice(vars.invoiceId, vars.body),
    onSuccess: invalidate,
  });
}

/**
 * Anula um lançamento **manual** (só `origin: manual` — o que veio do gateway
 * se desfaz no gateway). Pode devolver a fatura a `PENDING` e recomputar o
 * contrato, por isso invalida a entidade inteira.
 */
export function useInvalidateManualTransaction(
  invalidateKey: readonly unknown[],
) {
  const repository = container.resolve(SalesRepositoryToken);
  const invalidate = useInvalidate(invalidateKey);
  return useMutation({
    mutationFn: (vars: { transactionId: string; body: JustifiedActionInput }) =>
      repository.invalidateManualTransaction(vars.transactionId, vars.body),
    onSuccess: invalidate,
  });
}

/**
 * Desfaz o "não abrir de novo para esta entidade" de um alerta fechado: ele
 * segue fechado, e a entidade volta a ser vigiada.
 */
export function useReopenOperationalAlertForEntity(
  invalidateKey: readonly unknown[],
) {
  const repository = container.resolve(SalesRepositoryToken);
  const invalidate = useInvalidate(invalidateKey);
  return useMutation({
    mutationFn: (vars: { alertId: string; note: string }) =>
      repository.reopenOperationalAlertForEntity(vars.alertId, {
        note: vars.note,
      }),
    onSuccess: invalidate,
  });
}
